/**
 * NavigationActionHandler - Centralized navigation logic
 * Handles all navigation actions from dashboard and other scenes
 */

// Import necessary services
import { professionalNotifications } from './ProfessionalNotificationSystem.js';
import { gameInitializationCoordinator } from './GameInitializationCoordinator.js';
import { baseSceneIntegration } from './BaseSceneIntegration.js';
import { performanceMonitoring } from './PerformanceMonitoringSystem.js';

/**
 * Centralized navigation action handler
 * Eliminates duplicate navigation logic across scenes
 */
export class NavigationActionHandler {
    static _instance = null;
    
    constructor() {
        this.transitionInProgress = false;
        this.currentScene = null;
    }
    
    static getInstance() {
        if (!this._instance) {
            this._instance = new NavigationActionHandler();
        }
        return this._instance;
    }
    
    /**
     * Set current scene context
     * @param {Object} scene - Current scene instance
     */
    setCurrentScene(scene) {
        this.currentScene = scene;
    }
    
    /**
     * Handle navigation action (dashboard, analytics, etc.)
     * @param {string} action - Action to handle
     * @param {Object} scene - Scene context
     * @returns {boolean} - Success status
     */
    handleAction(action, scene) {
        if (this.transitionInProgress) {
            console.log('🔒 Navigation blocked - transition in progress');
            return false;
        }
        
        this.transitionInProgress = true;
        
        console.log(`🧭 NavigationActionHandler: Handling action "${action}" from ${scene.scene?.key || 'unknown'} (${this._getDeviceType()})`);
        
        // Record performance metrics
        const startTime = performance.now();
        
        try {
            let success = false;
            
            switch (action) {
                case 'dashboard':
                    success = this._navigateToDashboard(scene);
                    break;
                case 'analytics':
                    success = this._navigateToAnalytics(scene);
                    break;
                case 'ppt-hero-analytics':
                    success = this._navigateToPPTHeroAnalytics(scene);
                    break;
                case 'hall-of-fame':
                    success = this._navigateToHallOfFame(scene);
                    break;
                case 'game-center':
                    success = this._navigateToGameCenter(scene);
                    break;
                case 'profile':
                    success = this._navigateToProfile(scene);
                    break;
                case 'help':
                    success = this._navigateToHelp(scene);
                    break;
                default:
                    console.warn(`❓ Unknown action: ${action}`);
                    this.transitionInProgress = false;
                    return false;
            }
            
            // Record transition performance
            const duration = performance.now() - startTime;
            if (success && duration < 500) {
                console.log(`✅ Fast transition: ${scene.scene?.key} → ${this._getTargetScene(action)} (${duration.toFixed(2)}ms)`);
                performanceMonitoring.recordTransition(scene.scene?.key, this._getTargetScene(action), duration);
            }
            
            return success;
            
        } catch (error) {
            console.error('❌ Navigation error:', error);
            this.transitionInProgress = false;
            return false;
        }
    }
    
    /**
     * Handle game launch action
     * @param {string} game - Game to launch
     * @param {Object} scene - Scene context
     * @returns {boolean} - Success status
     */
    async handleGameAction(game, sourceScene = null) {
        if (this.transitionInProgress) {
            console.log('🔒 Game launch blocked - transition in progress');
            return false;
        }
        
        const scene = sourceScene || this.currentScene;
        if (!scene) {
            console.error('🚨 NavigationActionHandler: No scene context for game action');
            return false;
        }
        
        console.log(`🎮 NavigationActionHandler: Handling game "${game}" from ${scene.scene.key}`);
        
        // ENTERPRISE SOLUTION: Use GameInitializationCoordinator for proper timing
        try {
            if (game === 'ppt-hero') {
                professionalNotifications.success('Launching PPT Hero...', { duration: 2000 });
                
                // SIMPLE FIX: Use existing dashboard method that calls removeExistingOverlay
                if (scene.startPPTHero && typeof scene.startPPTHero === 'function') {
                    console.log('🎮 Using dashboard startPPTHero method');
                    await scene.startPPTHero();
                } else {
                    // Fallback for other scenes
                    console.log('🎮 Fallback: Direct scene transition');
                    scene.startScene('PPTHeroGenericScene');
                }
                
                this.transitionInProgress = false;
                return true;
            } else if (game === 'philosophy-flow') {
                professionalNotifications.success('Launching Philosophy Flow...', { duration: 2000 });
                // Philosophy Flow Desktop is a DOM-based scene, NOT a Phaser game
                // Use direct scene transition instead of GameInitializationCoordinator
                scene.startScene('PhilosophyFlowDesktopScene');
                this.transitionInProgress = false;
                return true;
            } else if (game === 'slide-wars') {
                professionalNotifications.warning('Slide Wars coming soon...', { duration: 3000 });
                this.transitionInProgress = false;
                return false;
            } else if (game === 'classify-tech') {
                professionalNotifications.success('Launching Classify Tech...', { duration: 2000 });
                return await gameInitializationCoordinator.initializeGame(scene, 'ClassifyScene');
            } else {
                console.warn(`❓ Unknown game: ${game}`);
                this.transitionInProgress = false;
                return false;
            }
        } catch (error) {
            console.error('❌ Game launch error:', error);
            this.transitionInProgress = false;
            return false;
        }
    }
    
    /**
     * Handle template actions (analytics, profile, etc.)
     * @param {string} action - The template action to handle
     * @param {object} scene - The current scene
     * @returns {Promise<boolean>} Success status
     */
    async handleTemplateAction(action, scene) {
        console.log(`🎯 NavigationActionHandler: Handling template action "${action}" from ${scene.scene?.key || 'unknown scene'}`);
        
        if (this.transitionInProgress) {
            console.log('⏳ Template action ignored - transition in progress');
            return false;
        }
        
        this.transitionInProgress = true;
        
        try {
            switch (action) {
                case 'dashboard':
                    // Navigate back to dashboard or do nothing if already there
                    if (scene.scene.key === 'AuthenticatedMenuSceneDOM') {
                        console.log('✅ Already on dashboard');
                        this.transitionInProgress = false;
                        return true;
                    }
                    professionalNotifications.success('Returning to Dashboard...', { duration: 1500 });
                    return this._navigateToScene(scene, 'AuthenticatedMenuSceneDOM');
                
                case 'analytics':
                    // Generic analytics - always go to AnalyticsSceneDOM template
                    professionalNotifications.success('Opening Analytics...', { duration: 1500 });
                    return this._navigateToScene(scene, 'AnalyticsSceneDOM');
                    
                case 'my-library':
                    professionalNotifications.success('Opening My Library...', { duration: 1500 });
                    return this._navigateToScene(scene, 'MyLibrarySceneDOM');
                    
                case 'profile':
                case 'preferences':
                    professionalNotifications.success('Opening Profile...', { duration: 1500 });
                    return this._navigateToScene(scene, 'ProfileSceneDOMTemplate');
                    
                case 'hall-of-fame':
                    professionalNotifications.success('Opening Hall of Fame...', { duration: 1500 });
                    return this._navigateToScene(scene, 'HallOfFameDesktopTemplate');
                    
                case 'game-center':
                    professionalNotifications.success('Opening Game Center...', { duration: 1500 });
                    return this._navigateToScene(scene, 'GameCenterSceneDOM');
                    
                case 'ppt-hero-analytics':
                    // Use scene's PPT Hero Analytics method if available
                    if (scene.openPPTHeroAnalytics && typeof scene.openPPTHeroAnalytics === 'function') {
                        professionalNotifications.success('Opening PPT Hero Analytics...', { duration: 1500 });
                        scene.openPPTHeroAnalytics();
                        this.transitionInProgress = false;
                        return true;
                    }
                    // Fallback: navigate to dashboard and trigger analytics
                    professionalNotifications.success('Opening PPT Hero Analytics...', { duration: 1500 });
                    if (scene.scene.key !== 'AuthenticatedMenuSceneDOM') {
                        // Navigate to dashboard first
                        this._navigateToScene(scene, 'AuthenticatedMenuSceneDOM');
                        setTimeout(() => {
                            const dashboardScene = window.game?.scene?.getScene('AuthenticatedMenuSceneDOM');
                            if (dashboardScene?.openPPTHeroAnalytics) {
                                dashboardScene.openPPTHeroAnalytics();
                            }
                        }, 300);
                    }
                    return true;
                    
                case 'admin-panel':
                    professionalNotifications.success('Opening Admin Panel...', { duration: 1500 });
                    return this._navigateToScene(scene, 'AdminPanelSceneDOM');
                    
                case 'notifications':
                    professionalNotifications.success('Opening Notifications...', { duration: 1500 });
                    return this._navigateToScene(scene, 'NotificationsSceneDOM');
                    
                case 'logout':
                    // Handle logout by calling scene's handleLogout if available
                    if (scene.handleLogout && typeof scene.handleLogout === 'function') {
                        professionalNotifications.info('Logging out...', { duration: 1500 });
                        scene.handleLogout();
                        this.transitionInProgress = false;
                        return true;
                    }
                    console.warn('❓ Scene does not have handleLogout method');
                    this.transitionInProgress = false;
                    return false;
                    
                default:
                    console.warn(`❓ Unknown template action: ${action}`);
                    this.transitionInProgress = false;
                    return false;
            }
        } catch (error) {
            console.error('❌ Template action error:', error);
            this.transitionInProgress = false;
            return false;
        }
    }
    
    // Private navigation methods
    _navigateToScene(scene, targetScene) {
        if (scene.scene.key === targetScene) {
            // Already on target scene
            this.transitionInProgress = false;
            return true;
        }
        
        if (window.sceneManager) {
            window.sceneManager.switchToScene(targetScene);
        } else {
            scene.startScene(targetScene);
        }
        
        this.transitionInProgress = false;
        return true;
    }
    
    _navigateToDashboard(scene) {
        if (scene.scene.key === 'AuthenticatedMenuSceneDOM') {
            // Already on dashboard
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'AuthenticatedMenuSceneDOM');
    }
    
    _navigateToAnalytics(scene) {
        if (scene.scene.key === 'AnalyticsSceneDOM') {
            // Already on analytics
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'AnalyticsSceneDOM');
    }
    
    _navigateToPPTHeroAnalytics(scene) {
        console.log('📈 Opening PPT Hero analytics via dashboard...');
        
        // FIXED: Ensure we're on dashboard first, then trigger analytics
        if (scene.scene.key === 'AuthenticatedMenuSceneDOM') {
            // Already on dashboard, trigger analytics directly
            if (scene.openPPTHeroAnalytics) {
                scene.openPPTHeroAnalytics();
                this.transitionInProgress = false;
                return true;
            }
        }
        
        // Navigate to dashboard first
        const success = this._performTransition(scene, 'AuthenticatedMenuSceneDOM');
        if (success) {
            // Wait for dashboard to be ready, then trigger analytics
            setTimeout(() => {
                const dashboardScene = window.game?.scene?.getScene('AuthenticatedMenuSceneDOM');
                if (dashboardScene && dashboardScene.openPPTHeroAnalytics) {
                    dashboardScene.openPPTHeroAnalytics();
                } else {
                    console.warn('📈 PPT Hero analytics not available after transition');
                }
            }, 200); // Increased timing for reliability
        }
        
        return success;
    }
    
    _navigateToHallOfFame(scene) {
        if (scene.scene.key === 'HallOfFameDesktopTemplate') {
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'HallOfFameDesktopTemplate');
    }
    
    _navigateToGameCenter(scene) {
        if (scene.scene.key === 'GameCenterSceneDOM') {
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'GameCenterSceneDOM');
    }
    
    _navigateToProfile(scene) {
        if (scene.scene.key === 'ProfileSceneDOMTemplate') {
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'ProfileSceneDOMTemplate');
    }
    
    _navigateToAdminPanel(scene) {
        if (scene.scene.key === 'AdminPanelSceneDOM') {
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'AdminPanelSceneDOM');
    }
    
    _navigateToNotifications(scene) {
        if (scene.scene.key === 'NotificationsSceneDOM') {
            this.transitionInProgress = false;
            return true;
        }
        return this._performTransition(scene, 'NotificationsSceneDOM');
    }
    
    _navigateToHelp(scene) {
        professionalNotifications.info('Help & Support coming soon...', { duration: 3000 });
        this.transitionInProgress = false;
        return true;
    }
    
    /**
     * Perform scene transition using enterprise services
     * @private
     */
    _performTransition(scene, targetScene) {
        try {
            // Use enhanced transition system
            return baseSceneIntegration.performEnhancedTransition(scene, targetScene);
        } catch (error) {
            console.error('❌ Transition failed:', error);
            this.transitionInProgress = false;
            return false;
        }
    }
    
    /**
     * Get target scene for action
     * @private
     */
    _getTargetScene(action) {
        const mapping = {
            'dashboard': 'AuthenticatedMenuSceneDOM',
            'analytics': 'AnalyticsSceneDOM',
            'ppt-hero-analytics': 'AuthenticatedMenuSceneDOM',
            'hall-of-fame': 'HallOfFameDesktopTemplate',
            'game-center': 'GameCenterSceneDOM',
            'profile': 'ProfileSceneDOMTemplate',
            'preferences': 'ProfileSceneDOMTemplate',
            'admin-panel': 'AdminPanelSceneDOM',
            'notifications': 'NotificationsSceneDOM'
        };
        return mapping[action] || 'Unknown';
    }
    
    /**
     * Get device type for logging
     * @private
     */
    _getDeviceType() {
        return window.innerWidth <= 768 ? 'mobile' : 'desktop';
    }
    
    /**
     * Reset transition state (for debugging)
     */
    resetTransitionState() {
        this.transitionInProgress = false;
        console.log('🔄 NavigationActionHandler: Transition state reset');
    }
    
    /**
     * Get current transition status
     * @returns {boolean} - Whether transition is in progress
     */
    isTransitioning() {
        return this.transitionInProgress;
    }
}

// Export singleton instance for easy access
export const navigationHandler = NavigationActionHandler.getInstance();
export default navigationHandler;